/**
 * The theme layer's single source of truth — keys, the cookie contract, and the
 * pure functions the server and the client both resolve the theme with
 * (VALE-520 · DARK-3).
 *
 * WHY A COOKIE AT ALL, when `next-themes` already ships an anti-FOUC script.
 *
 * The load-bearing constraint is the FIRST PAINT of a FLAG-GATED theme. Feature
 * flags arrive asynchronously from `/profile`; a blocking script that must run
 * before the browser paints cannot await a fetch. So the flag cannot be read at
 * paint time — but the cookie can, because the server sees it on the request
 * and can put the theme class straight onto `<html>` in the HTML it returns.
 *
 * That works because of one invariant, and the whole design rests on it:
 *
 *   THE THEME COOKIES ARE ONLY EVER WRITTEN WHILE THE `dark-theme` FLAG IS ON.
 *
 * So "a theme cookie exists" is a paint-time-safe proxy for "this user has the
 * flag". A flag-off user has no cookie, the server emits no theme class, and
 * there is nothing async on the critical path. `next-themes` has no native
 * cookie storage (pacocoursey/next-themes#294 is unmerged), so the mirror is
 * ours — `components/theme/vale-theme-provider.tsx` writes it, this module
 * describes it, and `app/layout.tsx` reads it.
 *
 * Everything here is pure and environment-free on purpose: the server reads
 * cookies through `next/headers`, the client writes them through
 * `document.cookie`, and neither concern belongs in the resolution logic that
 * both of them have to agree on.
 */

/** The `dark-theme` feature flag — the single gate the whole initiative hides behind. */
export const DARK_THEME_FLAG = 'dark-theme';

/**
 * The user's CHOICE: `light`, `dark`, or `system`. Mirrors what `next-themes`
 * holds in `localStorage` under the same key, so the two cannot disagree about
 * what the user picked.
 */
export const THEME_CHOICE_COOKIE = 'vale-theme';

/**
 * The RESOLVED theme (`light` or `dark`) — what `system` currently evaluates to.
 *
 * A second cookie rather than a cleverer encoding of the first, because the
 * server genuinely cannot resolve `system`: `prefers-color-scheme` is a client
 * fact, and the client hint that would carry it (`Sec-CH-Prefers-Color-Scheme`)
 * is not negotiated here. Storing the resolution lets the server paint a
 * `system` user's real theme on the first frame instead of guessing light.
 *
 * It can go stale in exactly one case — the OS preference changed between two
 * visits — and `next-themes` corrects that after hydration. That is a known,
 * bounded trade-off, and it is strictly better than the alternative, which is
 * every `system` user flashing light on every load.
 */
export const THEME_RESOLVED_COOKIE = 'vale-theme-resolved';

/**
 * The `localStorage` key `next-themes` persists the choice under. Deliberately
 * the same string as the choice cookie: one name for one concept, in both
 * stores, so a mismatch is impossible to introduce by editing one of them.
 */
export const THEME_STORAGE_KEY = THEME_CHOICE_COOKIE;

/** A year. Long enough that a preference outlives any plausible session. */
export const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** The theme a user can choose. */
export type ThemeChoice = 'light' | 'dark' | 'system';

/** The theme actually rendered — `system` has already been resolved away. */
export type ResolvedTheme = 'light' | 'dark';

const THEME_CHOICES: readonly string[] = ['light', 'dark', 'system'];
const RESOLVED_THEMES: readonly string[] = ['light', 'dark'];

/** The class `attribute="class"` themes with. Light is the absence of a class. */
export const DARK_CLASS = 'dark';

/** Narrow an untrusted cookie value to a choice, or `undefined`. */
export function parseThemeChoice(
  value: string | undefined
): ThemeChoice | undefined {
  return value !== undefined && THEME_CHOICES.includes(value)
    ? (value as ThemeChoice)
    : undefined;
}

/** Narrow an untrusted cookie value to a resolved theme, or `undefined`. */
export function parseResolvedTheme(
  value: string | undefined
): ResolvedTheme | undefined {
  return value !== undefined && RESOLVED_THEMES.includes(value)
    ? (value as ResolvedTheme)
    : undefined;
}

/** What the server needs in order to render the first frame. */
export interface InitialTheme {
  /**
   * The user's stored choice, or `undefined` when there is no cookie. Also the
   * signal that this user has the flag — see the invariant at the top.
   */
  choice: ThemeChoice | undefined;
  /** The theme to paint. Always concrete; light when nothing says otherwise. */
  resolved: ResolvedTheme;
  /** The class to put on `<html>`, or `undefined` for light. */
  themeClass: string | undefined;
}

/**
 * Resolve the first-paint theme from the two raw cookie values.
 *
 * The choice is authoritative and the resolved cookie is consulted only for
 * `system` — deliberately, so a stale resolved cookie can never contradict an
 * explicit choice. A user who picked `light` gets light even if the resolved
 * cookie somehow says otherwise.
 */
export function resolveInitialTheme(
  rawChoice: string | undefined,
  rawResolved: string | undefined
): InitialTheme {
  const choice = parseThemeChoice(rawChoice);

  let resolved: ResolvedTheme;
  if (choice === 'dark' || choice === 'light') {
    resolved = choice;
  } else if (choice === 'system') {
    resolved = parseResolvedTheme(rawResolved) ?? 'light';
  } else {
    // No cookie at all — no flag, so light, with no async dependency.
    resolved = 'light';
  }

  return {
    choice,
    resolved,
    themeClass: resolved === 'dark' ? DARK_CLASS : undefined
  };
}

/**
 * Serialise one theme cookie. Returned as a string rather than written here so
 * the logic stays testable without a DOM, and so the one place that touches
 * `document.cookie` is obvious.
 *
 * `SameSite=Lax` and no `HttpOnly`: the client has to write it, and it carries
 * a colour preference — there is nothing here to protect from a same-site
 * navigation. `Secure` follows the page, so a plain-http dev origin still works.
 */
export function serialiseThemeCookie(
  name: string,
  value: string,
  { secure }: { secure: boolean }
): string {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax'
  ];
  if (secure) parts.push('Secure');
  return parts.join('; ');
}

/** Serialise the expiry of one theme cookie. */
export function serialiseThemeCookieRemoval(name: string): string {
  return `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}
